import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { EntityDialog } from "../components/common/EntityDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Members, SHGs } from "../lib/api";
import { formatIndianDate } from "../lib/format";
import { toast } from "sonner";
import { CONSENT_OPTS, CONSENT_METHODS, validateConsent, WhatsAppBadge } from "../lib/mobile";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";

const MembersPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [shgs, setShgs] = useState([]);
  const [shgFilter, setShgFilter] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [membersData, shgsData] = await Promise.all([
        Members.list({ include_inactive: includeInactive, ...(shgFilter !== "all" ? { shg_id: shgFilter } : {}) }),
        SHGs.list({}),
      ]);
      setRows(membersData); setShgs(shgsData);
    } catch (e) {
      toast.error(e.message || "Failed to load members");
    } finally { setLoading(false); }
  }, [includeInactive, shgFilter]);

  useEffect(() => { load(); }, [load]);

  const shgOptions = useMemo(
    () => shgs.map((s) => ({ value: s.id, label: `${s.name} (${s.code})` })),
    [shgs],
  );
  const shgNameById = useMemo(() => { const m = {}; shgs.forEach((s) => { m[s.id] = s.name; }); return m; }, [shgs]);

  const fields = [
    { name: "shg_id", label: "Parent SHG", type: "select", required: true, options: shgOptions, placeholder: "Select SHG" },
    { name: "name", label: "Member name", required: true, halfWidth: true },
    { name: "code", label: "Member code", required: true, halfWidth: true, placeholder: "e.g. M-10001" },
    { name: "phone", label: "Mobile number", type: "mobile", halfWidth: true, placeholder: "10-digit mobile" },
    { name: "whatsapp_consent", label: "WhatsApp consent", type: "select", halfWidth: true, options: CONSENT_OPTS },
    { name: "consent_date", label: "Consent date", type: "date", halfWidth: true, showIf: (v) => v.whatsapp_consent === "yes" },
    { name: "consent_method", label: "How consent was recorded", type: "select", halfWidth: true, options: CONSENT_METHODS, showIf: (v) => v.whatsapp_consent === "yes" },
    { name: "joining_date", label: "Joining date", type: "date", halfWidth: true },
  ];
  const editFields = [
    ...fields,
    { name: "status", label: "Status", type: "select", halfWidth: true, options: [
      { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
    ] },
  ];

  const handleCreate = async (v) => { await Members.create(v); toast.success("Member created"); await load(); };
  const handleUpdate = async (v) => { await Members.update(editing.id, v); toast.success("Member updated"); await load(); };
  const toggleStatus = async (row) => {
    if (row.status === "active") { await Members.deactivate(row.id); toast.success(`${row.name} deactivated`); }
    else { await Members.activate(row.id); toast.success(`${row.name} activated`); }
    await load();
  };

  const columns = [
    { key: "code", header: "Code", mono: true, strong: true },
    { key: "name", header: "Member name", strong: true },
    { key: "shg_id", header: "SHG", render: (r) => shgNameById[r.shg_id] || "—" },
    { key: "phone", header: "Phone", mono: true },
    { key: "whatsapp_consent", header: "WhatsApp", render: (r) => <WhatsAppBadge row={r} /> },
    { key: "joining_date", header: "Joined", mono: true, render: (r) => formatIndianDate(r.joining_date) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status === "active" ? "Active" : "Closed"} /> },
    { key: "_actions", header: "Actions", render: (r) => (
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" data-testid={`member-edit-${r.id}`}
          onClick={() => { setEditing(r); setDialogOpen(true); }} className="h-8 px-2">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" data-testid={`member-toggle-${r.id}`}
          onClick={() => setConfirm({ row: r, action: r.status === "active" ? "deactivate" : "activate" })}
          className={`h-8 px-2 ${r.status === "active" ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
          {r.status === "active" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
        </Button>
      </div>
    )},
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={t.nav.members} subtitle="Members registered across SHGs"
        testId="members-page-header" showSampleBadge={false}
        actions={
          <>
            <Select value={shgFilter} onValueChange={setShgFilter}>
              <SelectTrigger className="h-9 w-56 bg-white" data-testid="member-shg-filter">
                <SelectValue placeholder="Filter by SHG" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All SHGs</SelectItem>
                {shgs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs text-emerald-900/70">
              <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} data-testid="toggle-include-inactive" />
              Show inactive
            </label>
            <Button data-testid="member-add-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              disabled={shgs.length === 0}
              className="bg-[#0F4C3A] text-white hover:bg-emerald-900">
              <Plus className="mr-1.5 h-4 w-4" />Add Member
            </Button>
          </>
        }
      />

      {shgs.length === 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          Please register at least one SHG before adding members.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">Loading…</div>
      ) : (
        <DataTable columns={columns} rows={rows}
          searchKeys={["name", "code", "phone"]}
          testId="members-table"
          emptyLabel="No members yet. Click “Add Member” to register one under an SHG." />
      )}

      <EntityDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editing ? "Edit member" : "Register new member"}
        fields={editing ? editFields : fields}
        initialValues={editing ? { whatsapp_consent: "no", ...editing } : { status: "active", whatsapp_consent: "no" }}
        validate={validateConsent}
        onSubmit={editing ? handleUpdate : handleCreate}
        submitLabel={editing ? "Save changes" : "Create member"}
        testIdPrefix="member-dialog"
      />
      <ConfirmDialog
        open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.action === "deactivate" ? "Deactivate this member?" : "Reactivate this member?"}
        description={confirm?.action === "deactivate"
          ? `“${confirm?.row?.name}” will be hidden from active lists.`
          : `“${confirm?.row?.name}” will become active again.`}
        confirmLabel={confirm?.action === "deactivate" ? "Deactivate" : "Reactivate"}
        destructive={confirm?.action === "deactivate"}
        testId="member-confirm"
        onConfirm={async () => { await toggleStatus(confirm.row); setConfirm(null); }}
      />
    </div>
  );
};

export default MembersPage;
